import React, { useState, useEffect } from "react";
import FeedNewCard from "./FeedNewCard";
import { fetchFinancialNewsEventDetails } from "../../../api/feed/feedAPI";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import { formatTime } from "../../../utils/date/DateUtils";

function EventNewsDetailsView({ eventUuid, initialEventData = null }) {
  const [eventData, setEventData] = useState(initialEventData);
  const [newsList, setNewsList] = useState(initialEventData?.news || []);
  const [isLoading, setIsLoading] = useState(!initialEventData?.news);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!eventUuid) return;

    const loadEventDetails = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await fetchFinancialNewsEventDetails(eventUuid);
        if (res.success && res.data) {
          setEventData(res.data);
          setNewsList(res.data.news || []);
        } else {
          setError(res.message || "Failed to load event details.");
        }
      } catch (err) {
        console.error("Failed to load event details:", err);
        setError("An unexpected error occurred.");
      } finally {
        setIsLoading(false);
      }
    };

    loadEventDetails();
  }, [eventUuid]);

  if (isLoading) {
    return (
      <div style={{ padding: "20px 0" }}>
        <LoadingPlaceholder rows={3} type="card" />
      </div>
    );
  }

  if (error) {
    return <div className="feed-error-message">{error}</div>;
  }

  return (
    <div
      className="event-news-details-container"
      style={{ display: "flex", flexDirection: "column", gap: "20px" }}
    >
      {/* Optional Event Summary Card */}
      {eventData && (
        <div className="standard-container event-summary-card">
          <h3
            style={{
              margin: "0 0 8px 0",
              fontSize: "18px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            {eventData.name}
          </h3>
          {eventData.description && (
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "14px",
                color: "#4B5563",
                lineHeight: "1.5",
              }}
            >
              {eventData.description}
            </p>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "12px",
              color: "#9CA3AF",
            }}
          >
            {eventData.event_date && (
              <span>{formatTime(eventData.event_date)}</span>
            )}
            {eventData.news_count > 0 && (
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  padding: "2px 8px",
                  borderRadius: "12px",
                  backgroundColor: "#EFF6FF",
                  color: "#2563EB",
                }}
              >
                {eventData.news_count}{" "}
                {eventData.news_count === 1 ? "news story" : "news stories"}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Associated News List */}
      {newsList.length === 0 ? (
        <div className="feed-empty-message">
          No news stories found for this event.
        </div>
      ) : (
        newsList.map((item, index) => {
          const data = item.content || item;

          return (
            <div
              key={data.uuid || data.id || index}
              className="feed-item-wrapper"
            >
              <FeedNewCard
                id={data.uuid || data.id}
                title={data.title}
                description={data.description}
                date={data.new_date || data.date}
                image_url={data.image || data.image_url}
                source_link={data.source_link}
                source_name={data.source_name}
                likes={data.likes}
                dislikes={data.dislikes}
                loves={data.love || data.loves}
                userReaction={data.user_reaction}
                comments={data.comments}
              />
            </div>
          );
        })
      )}
    </div>
  );
}

export default EventNewsDetailsView;
